import { useState } from 'react'
import { createFileRoute, useParams, Link } from '@tanstack/react-router'
import {
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  RefreshCcw,
  Globe,
  Users,
  Target,
  Briefcase,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useProjects } from '@/hooks/useProjects'
import { PageHeader } from '@/components/shared/PageHeader'
import {
  useBootstrapContext,
  useProjectContext,
  type BootstrapKeyword,
} from '@/hooks/useBootstrapContext'

export const Route = createFileRoute('/_authenticated/projekty/$projectId/cold-start')({
  component: ColdStartPage,
})

const intentLabels: Record<string, string> = {
  informational: 'Informacyjna',
  commercial: 'Komercyjna',
  transactional: 'Transakcyjna',
  navigational: 'Nawigacyjna',
}

const intentColors: Record<string, string> = {
  informational: '#3b82f6',
  commercial: '#10b981',
  transactional: '#f59e0b',
  navigational: '#8b5cf6',
}

const confidenceLabels: Record<string, string> = {
  high: 'Wysoka',
  medium: 'Średnia',
  low: 'Niska',
}

function ColdStartPage() {
  const { projectId } = useParams({
    from: '/_authenticated/projekty/$projectId/cold-start',
  })
  const { data: projects, isLoading: projectsLoading } = useProjects()
  const project = projects?.find((p) => p.id === projectId)

  const { data: existingContext, isLoading: contextLoading } = useProjectContext(projectId)
  const { preview, previewResult, isPreviewing, bootstrap, bootstrapResult, isBootstrapping, reset } =
    useBootstrapContext(projectId)

  const [skipSerp, setSkipSerp] = useState(false)

  const isLoading = projectsLoading || contextLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--text-muted)' }} />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="space-y-4">
        <p style={{ color: 'var(--text-muted)' }}>Nie znaleziono projektu.</p>
        <Button variant="outline" asChild>
          <Link to="/projekty">Wróć do projektów</Link>
        </Button>
      </div>
    )
  }

  // Display layer: priorytet to bootstrapResult (świeży) > previewResult > istniejący kontekst z DB
  const displayBusinessContext =
    bootstrapResult?.business_context ??
    previewResult?.business_context ??
    existingContext?.businessContext ??
    null

  const displayKeywords: BootstrapKeyword[] =
    bootstrapResult?.target_keywords ??
    previewResult?.target_keywords ??
    existingContext?.keywords ??
    []

  const displayCompetitors =
    bootstrapResult?.competitors_suggested ??
    existingContext?.competitors.map((c) => c.domain) ??
    []

  const isBootstrapped = !!bootstrapResult || existingContext?.isBootstrapped
  const hasPreview = !!previewResult && !bootstrapResult
  const hasData = !!displayBusinessContext

  return (
    <div className="space-y-6">
      <PageHeader
        title="Kontekst SEO"
        breadcrumbItems={[
          { label: 'Projekty', to: '/projekty' },
          { label: project.name, to: `/projekty/${project.id}` },
          { label: 'Kontekst SEO' },
        ]}
        subtitle={
          <span style={{ color: 'var(--text-muted)' }}>
            Cold Start — wygeneruj kontekst biznesowy + frazy SEO + konkurentów dla świeżej domeny.
          </span>
        }
      />

      {/* Status istniejącego kontekstu */}
      {isBootstrapped && !hasPreview && !bootstrapResult && (
        <Card style={{ background: 'var(--bg-secondary)', borderColor: '#10b981' }}>
          <CardContent className="flex items-center gap-3 py-4">
            <CheckCircle2 className="h-5 w-5" style={{ color: '#10b981' }} />
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Kontekst został już wygenerowany
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {displayKeywords.length} fraz target, {displayCompetitors.length} konkurentów.
                Możesz wygenerować ponownie aby zaktualizować dane.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 1: Akcja — Preview */}
      {!hasPreview && !bootstrapResult && (
        <Card style={{ background: 'var(--bg-secondary)' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
              <Sparkles className="h-5 w-5" style={{ color: '#3b82f6' }} />
              Wygeneruj kontekst projektu
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Zeskanujemy stronę {project.url}, AI wykryje branżę i grupę docelową, wygeneruje 10-20
              fraz SEO oraz znajdzie konkurentów w wyszukiwarce. Czas: ~30-60 sekund.
            </p>

            <div className="flex items-center gap-2">
              <Checkbox
                id="skip-serp"
                checked={skipSerp}
                onCheckedChange={(checked) => setSkipSerp(checked === true)}
              />
              <Label htmlFor="skip-serp" className="text-sm cursor-pointer">
                Pomiń wyszukiwanie konkurentów (oszczędność DataForSEO)
              </Label>
            </div>

            <Button
              onClick={() => preview({ skip_serp: skipSerp })}
              disabled={isPreviewing}
              size="lg"
            >
              {isPreviewing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generuję podgląd...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  {isBootstrapped ? 'Regeneruj kontekst' : 'Wygeneruj podgląd'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Preview review + confirm */}
      {hasPreview && (
        <Card style={{ background: 'var(--bg-secondary)', borderColor: '#3b82f6' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
              <AlertTriangle className="h-5 w-5" style={{ color: '#f59e0b' }} />
              Podgląd — sprawdź i zatwierdź
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {previewResult?.notes}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => bootstrap({ skip_serp: skipSerp })}
                disabled={isBootstrapping}
              >
                {isBootstrapping ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Zapisuję...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Zatwierdź i zapisz (z konkurentami)
                  </>
                )}
              </Button>
              <Button
                onClick={() => reset()}
                variant="outline"
                disabled={isBootstrapping}
              >
                <RefreshCcw className="mr-2 h-4 w-4" />
                Anuluj
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Final result */}
      {bootstrapResult && (
        <Card style={{ background: 'var(--bg-secondary)', borderColor: '#10b981' }}>
          <CardContent className="flex items-center gap-3 py-4">
            <CheckCircle2 className="h-5 w-5" style={{ color: '#10b981' }} />
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Kontekst zapisany
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {bootstrapResult.target_keywords_count} fraz, {bootstrapResult.competitors_inserted}{' '}
                konkurentów. Następne kroki: {bootstrapResult.next_steps[0]}
              </p>
            </div>
            <Button asChild size="sm">
              <Link to="/projekty/$projectId/audyt" params={{ projectId: project.id }}>
                Uruchom audyt
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Display: Business Context */}
      {hasData && displayBusinessContext && (
        <Card style={{ background: 'var(--bg-secondary)' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
              <Briefcase className="h-5 w-5" />
              Kontekst biznesowy
              <Badge
                variant="outline"
                style={{
                  borderColor:
                    displayBusinessContext.confidence === 'high'
                      ? '#10b981'
                      : displayBusinessContext.confidence === 'medium'
                        ? '#f59e0b'
                        : '#ef4444',
                  color:
                    displayBusinessContext.confidence === 'high'
                      ? '#10b981'
                      : displayBusinessContext.confidence === 'medium'
                        ? '#f59e0b'
                        : '#ef4444',
                }}
              >
                Pewność: {confidenceLabels[displayBusinessContext.confidence]}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ContextRow label="Branża" value={displayBusinessContext.industry} />
            <ContextRow label="Klient idealny (ICP)" value={displayBusinessContext.icp} />
            <ContextRow label="Grupa docelowa" value={displayBusinessContext.target_audience} />
            <ContextRow label="Propozycja wartości" value={displayBusinessContext.value_proposition} />
            <ContextRow label="Język strony" value={displayBusinessContext.primary_language.toUpperCase()} />
          </CardContent>
        </Card>
      )}

      {/* Display: Keywords */}
      {displayKeywords.length > 0 && (
        <Card style={{ background: 'var(--bg-secondary)' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
              <Target className="h-5 w-5" />
              Frazy SEO ({displayKeywords.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {displayKeywords.map((kw, i) => (
                <div
                  key={`${kw.keyword}-${i}`}
                  className="flex items-start gap-3 rounded-md border p-3"
                  style={{ background: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}
                >
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: kw.priority >= 8 ? '#ef4444' : kw.priority >= 5 ? '#f59e0b' : '#6b7280' }}
                  >
                    {kw.priority}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                        {kw.keyword}
                      </span>
                      <Badge
                        variant="outline"
                        className="text-xs"
                        style={{ borderColor: intentColors[kw.intent], color: intentColors[kw.intent] }}
                      >
                        {intentLabels[kw.intent]}
                      </Badge>
                    </div>
                    {kw.rationale && (
                      <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                        {kw.rationale}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Display: Competitors */}
      {displayCompetitors.length > 0 && (
        <Card style={{ background: 'var(--bg-secondary)' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base" style={{ color: 'var(--text-primary)' }}>
              <Users className="h-5 w-5" />
              Sugerowani konkurenci ({displayCompetitors.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {displayCompetitors.map((domain) => (
                <a
                  key={domain}
                  href={`https://${domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs hover:underline"
                  style={{
                    background: 'var(--bg-primary)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <Globe className="h-3 w-3" />
                  {domain}
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function ContextRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[200px_1fr] sm:gap-4">
      <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  )
}
